import React, { useEffect, useMemo, useState } from "react";
import { Search, Download, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TYPE_LABELS = {
  tip: "Tip",
  channel_sub_gc: "Channel Sub (GC)",
  channel_sub_stripe: "Channel Sub (Stripe)",
  platform_sub_gc: "Platform Sub (GC)",
  platform_sub_stripe: "Platform Sub",
  sub_stripe: "Platform Sub (legacy)",
  coin_pack_sale: "Coin Pack",
  credit_pack: "Coin Pack (legacy)",
  payout: "Payout",
  reversal: "Reversal",
};

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week",  label: "7 days" },
  { key: "month", label: "30 days" },
  { key: "year",  label: "1 year" },
  { key: "all",   label: "Lifetime" },
];

function rangeStart(key) {
  const now = new Date();
  const d = new Date(now);
  switch (key) {
    case "today": d.setHours(0, 0, 0, 0); return d;
    case "week":  d.setDate(now.getDate() - 7); return d;
    case "month": d.setDate(now.getDate() - 30); return d;
    case "year":  d.setFullYear(now.getFullYear() - 1); return d;
    default: return null;
  }
}

export default function Transactions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [range, setRange] = useState("month");

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (type) params.type = type;
    api.get("/earnings/transactions", { params })
      .then((r) => setRows(r.data.transactions || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [type]);

  const filtered = useMemo(() => {
    const start = rangeStart(range);
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (start && new Date(r.at) < start) return false;
      if (needle && !((r.from_username || "").toLowerCase().includes(needle) || (r.type || "").includes(needle))) return false;
      return true;
    });
  }, [rows, q, range]);

  const totals = useMemo(() => {
    let gross = 0, share = 0, fee = 0;
    filtered.forEach((r) => {
      gross += r.gross_usd_cents || 0;
      share += r.streamer_share_cents || 0;
      fee   += r.platform_fee_cents || 0;
    });
    return { gross, share, fee };
  }, [filtered]);

  const exportCsv = () => {
    if (filtered.length === 0) return toast.error("No rows to export");
    const header = ["Date", "Type", "From", "Gross USD", "Your Share USD", "Platform Fee USD", "Source", "Status"];
    const csvEsc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(csvEsc).join(",")];
    filtered.forEach((r) => {
      lines.push([
        r.at,
        TYPE_LABELS[r.type] || r.type,
        r.from_username || "",
        (r.gross_usd_cents / 100).toFixed(2),
        (r.streamer_share_cents / 100).toFixed(2),
        (r.platform_fee_cents / 100).toFixed(2),
        r.source || "",
        r.paid_out ? "paid" : "pending",
      ].map(csvEsc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mysticast_transactions_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-4" data-testid="section-transactions">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <SumCard label="GROSS"        value={`$${(totals.gross / 100).toFixed(2)}`}  color="cyan"   testid="tx-total-gross" />
        <SumCard label="YOUR SHARE"   value={`$${(totals.share / 100).toFixed(2)}`}  color="volt"   testid="tx-total-share" />
        <SumCard label="PLATFORM FEE" value={`$${(totals.fee   / 100).toFixed(2)}`}  color="pink"   testid="tx-total-fee" />
      </div>

      {/* Controls */}
      <div className="term-card p-3">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search from-user or type…"
              className="w-full pl-9 pr-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
              data-testid="tx-search"
            />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 bg-[#0F0F14] border border-white/10 rounded sc-mono text-xs" data-testid="tx-type-filter">
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="flex gap-1 flex-wrap" data-testid="tx-range-group">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-2.5 py-1.5 text-[10px] sc-mono rounded transition ${range === r.key ? "bg-[color:var(--sc-cyan)]/15 text-[color:var(--sc-cyan)] border border-[color:var(--sc-cyan)]/40" : "text-zinc-500 hover:text-zinc-200 border border-transparent"}`}
                data-testid={`tx-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="sc-btn-ghost text-xs px-2 py-2 flex items-center gap-1" data-testid="tx-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={exportCsv} className="sc-btn text-xs flex items-center gap-1.5" data-testid="tx-export-csv">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="term-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-xs text-zinc-500" data-testid="tx-empty">No transactions match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tx-table">
              <thead>
                <tr className="border-b border-white/10 text-zinc-500 sc-mono text-[10px]">
                  <th className="text-left px-3 py-2">DATE</th>
                  <th className="text-left px-3 py-2">TYPE</th>
                  <th className="text-left px-3 py-2">FROM</th>
                  <th className="text-right px-3 py-2">GROSS</th>
                  <th className="text-right px-3 py-2">SHARE</th>
                  <th className="text-right px-3 py-2">FEE</th>
                  <th className="text-left px-3 py-2">METHOD</th>
                  <th className="text-center px-3 py-2">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`tx-row-${r.id}`}>
                    <td className="px-3 py-2 sc-mono text-zinc-500 whitespace-nowrap">{new Date(r.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-3 py-2 text-zinc-300">{TYPE_LABELS[r.type] || r.type}</td>
                    <td className="px-3 py-2 text-zinc-400 truncate max-w-[140px]">{r.type === "payout" ? "→ your bank" : (r.from_username || "—")}</td>
                    <td className="px-3 py-2 text-right sc-mono">${(r.gross_usd_cents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right sc-mono font-bold" style={{ color: r.type === "payout" ? "var(--sc-orange)" : "var(--sc-volt)" }}>
                      {r.type === "payout" ? "-" : "+"}${(r.streamer_share_cents / 100).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right sc-mono text-zinc-500">${(r.platform_fee_cents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 sc-mono text-[10px] text-zinc-500">{r.source}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[9px] sc-mono ${r.paid_out ? "text-zinc-500" : "text-[color:var(--sc-volt)]"}`}>{r.paid_out ? "PAID" : "OPEN"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[10px] sc-mono text-zinc-500 text-center">
        Showing {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} transactions
      </div>
    </div>
  );
}

function SumCard({ label, value, color, testid }) {
  return (
    <div className={`term-card ${color} p-3`} data-testid={testid}>
      <div className="sc-label text-[10px]" style={{ color: `var(--sc-${color})` }}>{label}</div>
      <div className="sc-heading text-2xl font-black mt-1">{value}</div>
    </div>
  );
}
